import { saveAs } from 'file-saver';
import { noop } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '~/eds/src/components/Button/Button';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { useModal } from '~/eds/src/components/hooks/useModal';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Paginate } from '~/eds/src/components/Paginate/Paginate';
import { Section } from '~/eds/src/components/Section/Section';
import { Text } from '~/eds/src/components/Text/Text';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { Option } from '~/eds/src/types/Option';
import { scrollElementIntoView } from '~/eds/src/utils/scrollElementIntoView';
import { DataFieldType } from '~/enums/DataFieldType';
import { EntityType } from '~/enums/EntityType';
import { FileMimeType } from '~/enums/FileMimeType';
import { isActiveQuery } from '~/features/advanced-search/query-filters/QueryFilters.utils';
import { CreateFieldPanel } from '~/features/fields/CreateFieldPanel/CreateFieldPanel';
import { FieldInfo } from '~/features/fields/FieldInfo/FieldInfo';
import { FieldSelect } from '~/features/fields/FieldSelect/FieldSelect';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useErrors } from '~/hooks/useErrors';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';
import { DataField } from '~/types/Field';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';

import { MODEL_INSTRUCTIONS_ELEMENT_ID } from '../../constants';
import { FieldModel } from '../../fields/types';
import {
  isDataFieldUnavailable,
  sortDataFieldOptions,
  testIsClassificationModel,
  testIsMultiClassificationModel,
  testIsTestCaseEvaluating,
  testIsTextExtractionModel,
} from '../../fields/utils';
import { ManageLibraryModelDetailsTrigger } from '../../library/ModelLibraryAdmin/ManageLibraryModelDetailsTrigger';
import { PromptModelTestCase } from '../../types/deprecated';
import { TargetEntityDetails } from '../../types/entity';
import { OptimizeInfo } from '../../types/optimize';
import { ConfigureDropdownOptions } from './DropdownOptions/ConfigureDropdownOptions';
import { InstructionsEditor } from './InstructionsEditor';
import { LibraryModelInstructions } from './LibraryModelInstructions';
import { ModelScopeSelect } from './ModelScopeSelect';
import { SaveAndEvaluateButton } from './SaveAndEvaluateButton';

interface Props {
  isConfigDirty: boolean;
  modelVersion: Nullable<FieldModel>;
  targetEntityDetails: Nullable<TargetEntityDetails>;
  currentOptimizeVersion?: Nullable<OptimizeInfo>;
  readOnly?: boolean;
  onExit?: () => void;
}

export const Configuration = ({
  currentOptimizeVersion,
  isConfigDirty,
  modelVersion,
  readOnly = false,
  onExit,
  targetEntityDetails,
}: Props) => {
  const intl = useIntl();
  const { RETRY_ERROR } = useErrors();
  const dispatch = useDispatch();
  const { navigate, params } = useRouting();
  const { modelId = '' } = params;
  const { toast } = useToast();

  const enableDocumentXRayDownloadOptimize = useFlag(
    FlagType.DocumentXRayDebugOutput,
  );
  const enableXrayNewPricingModel = useFlag(
    FlagType.DocumentXrayNewPricingModel,
  );
  const enableDocumentXRayOptimize = useFlag(FlagType.DocumentXrayOptimize);
  const enablePrebuiltModelLibrary = useFlag(
    FlagType.DocumentXRayPrebuiltModels,
  );
  const enablePrebuiltModelsAdmin = useFlag(
    FlagType.DocumentXRayPrebuiltModelsAdmin,
  );
  const highlightMethod = useFlag(FlagType.CustomAiHighlightMethod);

  const field = useSelector(slices.xRay.selectors.selectField);
  const query = useSelector(slices.xRay.selectors.selectQuery);
  const instructions = useSelector(slices.xRay.selectors.selectInstructions);
  const config = useSelector(slices.xRay.selectors.selectConfiguration);
  const versions = useSelector(slices.xRay.selectors.selectModelVersions);
  const activeVersionNumber = useSelector(
    slices.xRay.selectors.selectActiveVersionNumber,
  );

  const activeQuery = isActiveQuery(query);

  const isNew = !modelVersion;

  const { data: fieldModelFieldIds, isFetching: isFetchingFieldModelFieldIds } =
    api.endpoints.getPromptModelFieldIds.useQuery({});

  const fieldModelIdsSet = useMemo(
    () => new Set(fieldModelFieldIds ?? []),
    [fieldModelFieldIds],
  );

  const [saveDraft, { isLoading: isLoadingSaveDraft }] =
    api.endpoints.savePromptModelDraft.useMutation();

  const [getOptimizeSummary, { isLoading: isFetchingOptimizeSummary }] =
    api.endpoints.getPromptModelOptimizeSummary.useLazyQuery();

  const [getFieldValues] = api.endpoints.getFieldValues.useLazyQuery();

  const [draftField, setDraftField] = useState<Nullable<DataField>>(null);

  const [
    isCreateFieldPanelVisible,
    _toggleCreateFieldModalVisible,
    showCreateFieldPanel,
    hideCreateFieldPanel,
  ] = useToggle(false);

  const hasNotStartedOrErrorTestCases = (test: PromptModelTestCase) =>
    test.state === 'error' || test.state === 'not_started';

  const hasNoTestCases = modelVersion?.testCases
    ? modelVersion?.testCases.length === 0
    : true;

  const hasTestCasesToEvaluate =
    modelVersion?.testCases && !hasNoTestCases
      ? modelVersion.testCases.some(hasNotStartedOrErrorTestCases)
      : false;

  const hasTestCasesToRetry =
    modelVersion?.testCases && !hasNoTestCases
      ? modelVersion.testCases.some((test) => test.state === 'error')
      : false;

  const hasEvaluatingTestCases =
    modelVersion?.testCases && !hasNoTestCases
      ? modelVersion.testCases.some(testIsTestCaseEvaluating)
      : false;

  const isEvaluating = hasEvaluatingTestCases || isLoadingSaveDraft;

  const handleConfirmField = async (updatedField: Nullable<DataField>) => {
    if (updatedField) {
      let fieldClassification;
      if (testIsClassificationModel(updatedField)) {
        try {
          const fieldValuesResponse = await getFieldValues({
            fieldId: String(updatedField.id),
          });
          const dataFieldOptions = (fieldValuesResponse.data?.values ?? []).map(
            (option) => ({
              label: option.display_value,
              value: String(option.value),
            }),
          );

          fieldClassification = {
            isMulti: testIsMultiClassificationModel(updatedField),
            options: dataFieldOptions,
          };
        } catch {
          toast({
            message: intl.formatMessage({
              id: 'XRayConfiguration.fieldSelect.errorFetchingFieldValues',
              defaultMessage: 'An error occurred while fetching field values.',
              description:
                'Error message when fetching field values fails in Custom AI configuration.',
            }),
            status: 'danger',
          });
        }
      }
      dispatch(
        slices.xRay.actions.setField({
          field: draftField,
          fieldClassification,
        }),
      );
    }
  };

  const [confirmFieldSelectModal, showConfirmFieldSelectModal] = useModal({
    children: (
      <Text>
        <FormattedMessage
          id="XRayConfiguration.fieldSelect.confirmFieldSelectionText"
          defaultMessage="Once confirmed, the field selection cannot be changed. You can exit and
            restart the process to automate a different field."
          description="Confirmation message for field selection in Custom AI configuration."
        />
      </Text>
    ),
    enableHideOnPrimaryAction: true,
    title: intl.formatMessage({
      id: 'XRayConfiguration.fieldSelect.confirmFieldSelectionTitle',
      defaultMessage: 'Confirm Field Selection',
      description:
        'Title for the modal confirming field selection in Custom AI configuration.',
    }),
    primaryAction: {
      text: intl.formatMessage({
        id: 'XRayConfiguration.fieldSelect.confirmButtonText',
        defaultMessage: 'Confirm',
        description: 'Text for the confirm button in field selection modal.',
      }),
      onClick: () => handleConfirmField(draftField),
    },
    onHide: () => setDraftField(field),
  });

  const mapOption = (
    option: Option<PilotId, DataField>,
  ): Option<PilotId, DataField> => {
    const { isSmart, type } = option.data!;
    const isUnavailable = isDataFieldUnavailable(type);
    const hasExistingModel = fieldModelIdsSet.has(option.value);
    const disabledTooltip = isUnavailable
      ? intl.formatMessage({
          id: 'XRayConfiguration.fieldSelect.unavailableDataTypeTooltip',
          defaultMessage:
            "This field is currently not available for model creation as it is of a data type that isn't currently supported.",
          description:
            'Tooltip for unavailable data field types in Custom AI configuration.',
        })
      : hasExistingModel
        ? intl.formatMessage({
            id: 'XRayConfiguration.fieldSelect.unavailablePreExistingTooltip',
            defaultMessage: 'This field already has a Custom AI model.',
            description:
              'Tooltip for unavailable pre-existing field types in Custom AI configuration.',
          })
        : isSmart
          ? intl.formatMessage({
              id: 'XRayConfiguration.fieldSelect.unavailableSmartFieldTooltip',
              defaultMessage:
                'Custom AI is not supported for Standard AI fields.',
              description:
                'Tooltip for unavailable smart fields in Custom AI configuration.',
            })
          : undefined;
    return {
      ...option,
      disabled: Boolean(disabledTooltip),
      tooltip: disabledTooltip,
    };
  };

  const fieldSelectFooterAction = {
    text: intl.formatMessage({
      id: 'XRayConfiguration.fieldSelect.footerActionText',
      defaultMessage: 'Create new field',
      description:
        'Text for button to create a new field in field select dropdown in Custom AI configuration.',
    }),
    level: 'tertiary' as const,
    mode: 'link' as const,
    onClick: showCreateFieldPanel,
  };

  const handleChangeDraftField = (updatedDraftField: Nullable<DataField>) => {
    setDraftField(updatedDraftField);
    showConfirmFieldSelectModal();
  };

  const handleChangeInstructions = (updatedInstructions: Nullable<string>) => {
    dispatch(slices.xRay.actions.setInstructions(updatedInstructions));
  };

  const handleEvaluate = () => {
    saveDraft({
      modelId: isNew ? null : modelId,
      config: {
        ...config,
        query: activeQuery,
      },
      highlightMethod,
      instructionsOptional,
    })
      .unwrap()
      .then((updatedModel) => {
        if (updatedModel) {
          if (isNew) {
            navigate(`${RoutePathType.AutomationHubFields}/${updatedModel.id}`);
          } else {
            dispatch(
              slices.xRay.actions.setDismissedIncorrectStatusMessage(false),
            );
            dispatch(slices.xRay.actions.setReviewIncorrectCount(0));
          }
        }
      })
      .catch((error) => {
        const message = error.response?.data?.detail ?? RETRY_ERROR;
        if (
          message === 'No (more) documents were found in the model scope.' ||
          error.message === 'No (more) documents were found in the model scope.'
        ) {
          // toast will be rendered in the Configuration component
          if (isNew) {
            const newModelId = error.cause;
            if (newModelId) {
              navigate(`${RoutePathType.AutomationHubFields}/${newModelId}`);
            }
          }
        } else {
          toast({
            message: intl.formatMessage({
              id: 'XRayConfiguration.saveAndEvaluate.errorMessage',
              defaultMessage:
                'No more documents were found in the model scope.',
              description:
                'Error message when no more documents are found in the model scope during save and evaluate in Custom AI configuration.',
            }),
            status: 'danger',
          });
        }
      });
  };

  const instructionsOptional = Boolean(
    enablePrebuiltModelLibrary &&
    modelVersion?.libraryModelId &&
    modelVersion?.libraryModelVersion &&
    !enablePrebuiltModelsAdmin,
  );
  const configRequrementsMet = Boolean(
    field && (instructionsOptional || instructions),
  );
  const shouldRetryTestCases =
    hasTestCasesToRetry && !isEvaluating && !isConfigDirty;

  const hasField = Boolean(field);

  const isScopeEmpty = enableXrayNewPricingModel && !activeQuery.length;

  useEffect(() => {
    if (modelVersion?.testCases?.length) {
      scrollElementIntoView(MODEL_INSTRUCTIONS_ELEMENT_ID, {
        behavior: 'smooth',
      });
    }
  }, [modelVersion?.id]);

  const handlePageIndexUpdate = (pageIndex: number) => {
    dispatch(slices.xRay.actions.setDocumentViewer({}));
    dispatch(slices.xRay.actions.setActiveVersionNumber(pageIndex));
  };

  const isClassificationModel = field && testIsClassificationModel(field);

  const isTextExtractionModel = field && testIsTextExtractionModel(field);

  const instructionsDescription = isTextExtractionModel
    ? intl.formatMessage({
        id: 'XRayConfiguration.instructions.extractionModelDescriptionText',
        defaultMessage:
          'Tell the model what to do or let Evisort AI suggest instructions. For best results, we recommend including a short definition of the field, and a few examples of the answer in the format that you prefer. e.g. If you want to extract email addresses, you can specify that you only want the AI to return “abc@evisort.com” or “xyz@evisort.com,” and no more additional text.',
        description:
          'Description for instructions field in Custom AI configuration of extraction models.',
      })
    : intl.formatMessage({
        id: 'XRayConfiguration.instructions.classificationModelDescriptionText',
        defaultMessage:
          'Tell the model what to do or let Evisort AI suggest instructions. You can also ask the model a question.',
        description:
          'Description for instructions field in Custom AI configuration of classification models.',
      });

  const instructionsPlaceholder = isTextExtractionModel
    ? intl.formatMessage({
        id: 'XRayConfiguration.instructions.extractionModelPlaceholderText',
        defaultMessage:
          'If there are external T&Cs, extract the URL where they appear. Format your answer as a URL, such as “www.abc.com/xyz” or “www.x-ray.com”.',
        description:
          'Placeholder text for instructions field in Custom AI configuration of extraction models.',
      })
    : intl.formatMessage({
        id: 'XRayConfiguration.instructions.classificationModelPlaceholderText',
        defaultMessage:
          'e.g. What is the vendor penalty if they can’t deliver?',
        description:
          'Placeholder text for instructions field in Custom AI configuration of classification models.',
      });

  const instructionsExample = isClassificationModel
    ? {
        content: intl.formatMessage({
          id: 'XRayConfiguration.insructions.exampleContent',
          defaultMessage:
            'Example Model Instructions:\r\n1. Find all the email addresses in the document. Return “abc@evisort.com” or “xyz@evisort.com” and no more additional text.\r\n2. Find all the funds names in the exhibits.\r\n3. Find all the signatories in the document. Return only their name, and not their titles. For example, “Jane Doe”, or “John Taylor”.',
          description:
            'Example content for instructions field in Custom AI configuration.',
        }),
        label: intl.formatMessage({
          id: 'XRayConfiguration.instructions.exampleLabel',
          defaultMessage: 'Show me examples',
          description:
            'Label for example instructions in Custom AI configuration.',
        }),
      }
    : undefined;

  const fieldId = field?.id ?? null;

  const fieldDescription = intl.formatMessage({
    id: 'XRayConfiguration.fieldSelect.description',
    defaultMessage:
      'Choose a field for the AI to populate. Once confirmed, the selection cannot be changed.',
    description: 'Description for field select in Custom AI configuration.',
  });

  const isMissingDropdownOptions = Boolean(
    config.fieldClassification &&
    !config.fieldClassification.isOpenEnded &&
    (!config.fieldClassification.options.length ||
      !config.fieldClassification.defaultOption),
  );

  return (
    <Section title="">
      <Layout preset="form-fields">
        <FormField<PilotId, false>
          description={
            isNew && enablePrebuiltModelLibrary ? undefined : fieldDescription
          }
          descriptionLink={
            isNew && enablePrebuiltModelLibrary
              ? {
                  additionalText: intl.formatMessage({
                    id: 'XRayConfiguration.fieldSelect.descriptionWithAdditionalText',
                    defaultMessage:
                      'Choose a field for the AI to populate. Once confirmed, the selection cannot be changed. Or create new from ',
                    description:
                      'Description for field select in Custom AI configuration with additional text for creating a model from Model Library.',
                  }),
                  callback: onExit,
                  linkText: intl.formatMessage({
                    id: 'XRayConfiguration.fieldSelect.descriptionLinkText',
                    defaultMessage: 'Model Library.',
                    description:
                      'Text for link to Model Library in Custom AI configuration.',
                  }),
                  pathname: isConfigDirty ? '' : RoutePathType.XRayLibrary,
                  search: isConfigDirty ? undefined : 'source=new',
                }
              : undefined
          }
          input={null}
          label={intl.formatMessage({
            id: 'XRayConfiguration.fieldSelect.label',
            defaultMessage: 'Field',
            description: 'Label for field select in Custom AI configuration.',
          })}
          name="field-select"
          value={fieldId}
          onChange={noop}
        >
          {fieldId ? (
            <FieldInfo id={fieldId} />
          ) : (
            <FieldSelect
              disabled={hasField}
              footerAction={fieldSelectFooterAction}
              isLoading={isFetchingFieldModelFieldIds}
              value={fieldId}
              mapOption={mapOption}
              onChange={handleChangeDraftField}
              sortOption={sortDataFieldOptions}
            />
          )}
        </FormField>
        {isClassificationModel && (
          <ConfigureDropdownOptions
            config={config}
            readOnly={Boolean(readOnly || modelVersion?.libraryModelId)}
            readOnlyTooltip={
              readOnly
                ? intl.formatMessage({
                    id: 'XRayConfiguration.dropdownOptions.formFieldTooltip',
                    defaultMessage: 'You are in read-only mode.',
                    description:
                      'Tooltip for disabled dropdown options configuration in read-only mode.',
                  })
                : modelVersion?.libraryModelId
                  ? intl.formatMessage({
                      id: 'XRayConfiguration.dropdownOptions.formFieldTooltipForLibraryModel',
                      defaultMessage: 'This configuration cannot be updated.',
                      description:
                        'Tooltip for disabled dropdown options for a library model configuration in read-only mode.',
                    })
                  : undefined
            }
          />
        )}
        <ModelScopeSelect
          entityId={fieldId ? String(fieldId) : null}
          entityType={EntityType.Field}
          readOnly={readOnly}
        />
        {enablePrebuiltModelLibrary &&
        modelVersion?.libraryModelId &&
        modelVersion?.libraryModelVersion &&
        !enablePrebuiltModelsAdmin ? (
          <LibraryModelInstructions
            libraryModelId={modelVersion.libraryModelId}
            libraryModelVersion={modelVersion.libraryModelVersion}
          />
        ) : (
          <FormField<string, false>
            description={instructionsDescription}
            example={instructionsExample}
            input={InstructionsEditor}
            label={intl.formatMessage({
              id: 'XRayConfiguration.instructions.label',
              defaultMessage: 'Instructions for the Model',
              description:
                'Label for instructions field in Custom AI configuration.',
            })}
            name={MODEL_INSTRUCTIONS_ELEMENT_ID}
            placeholder={instructionsPlaceholder}
            value={instructions}
            onChange={handleChangeInstructions}
            inputProps={{
              readOnly: readOnly,
            }}
          />
        )}
        <Layout wrap align="center" justify="space-between" w="100%">
          {versions.length > 1 && (
            <Paginate
              disableGoTo
              mode="count"
              name="model-version-paginate"
              pageIndex={activeVersionNumber}
              totalCount={versions.length}
              onUpdatePageIndex={handlePageIndexUpdate}
            />
          )}
          <SaveAndEvaluateButton
            configRequirementsMet={
              configRequrementsMet && !isScopeEmpty && !isMissingDropdownOptions
            }
            disabled={
              !configRequrementsMet ||
              isMissingDropdownOptions ||
              (!isConfigDirty && !hasNoTestCases && !hasTestCasesToEvaluate) ||
              isScopeEmpty ||
              readOnly
            }
            hasNoChanges={!isConfigDirty && !hasNoTestCases}
            isEvaluating={isEvaluating}
            isLoading={isLoadingSaveDraft}
            readOnly={readOnly}
            shouldRetry={shouldRetryTestCases}
            onClick={handleEvaluate}
          />
          {enableDocumentXRayDownloadOptimize && enableDocumentXRayOptimize && (
            <Button
              disabled={!currentOptimizeVersion}
              isLoading={isFetchingOptimizeSummary}
              tooltip={
                !currentOptimizeVersion
                  ? intl.formatMessage({
                      id: 'XRayConfiguration.boostSummaryDownload.disabledTooltip',
                      defaultMessage:
                        'This model has not been optimized. Please optimize the model before downloading the boost summary.',
                      description:
                        'Tooltip for disabled boost summary download button when the model has not been optimized.',
                    })
                  : isFetchingOptimizeSummary
                    ? intl.formatMessage({
                        id: 'XRayConfiguration.boostSummaryDownload.downloadingTooltip',
                        defaultMessage: 'Downloading boost summary…',
                        description:
                          'Tooltip for boost summary download button when the summary is being downloaded.',
                      })
                    : undefined
              }
              text={intl.formatMessage({
                id: 'XRayConfiguration.boostSummaryDownload.buttonText',
                defaultMessage: 'Boost Summary',
                description:
                  'Text for boost summary download button in Custom AI configuration.',
              })}
              onClick={() => {
                if (currentOptimizeVersion) {
                  getOptimizeSummary({
                    modelId,
                    version: currentOptimizeVersion.version,
                  }).then(({ isSuccess, data }) => {
                    const json = JSON.stringify(data, null, 2);
                    if (isSuccess) {
                      const file = new File([json], `${modelId}_boost.json`, {
                        type: FileMimeType.Json,
                      });
                      toast({
                        message: intl.formatMessage({
                          id: 'XRayConfiguration.boostSummaryDownload.successMessage',
                          defaultMessage:
                            'A file has been generated with the summary.',
                          description:
                            'Message when a file is successfully generated for the boost summary.',
                        }),
                        status: 'success',
                      });
                      saveAs(file);
                    } else {
                      toast({
                        message: intl.formatMessage({
                          id: 'XRayConfiguration.boostSummaryDownload.errorMessage',
                          defaultMessage:
                            'A file could not be generated for the boost summary.',
                          description:
                            'Message when a file cannot be generated for the boost summary.',
                        }),
                        status: 'danger',
                      });
                    }
                  });
                }
              }}
            />
          )}
          {enablePrebuiltModelsAdmin && (
            <ManageLibraryModelDetailsTrigger
              config={config}
              disabled={
                isConfigDirty ||
                hasNoTestCases ||
                !configRequrementsMet ||
                isMissingDropdownOptions ||
                isLoadingSaveDraft ||
                readOnly
              }
              targetEntityDetails={targetEntityDetails}
              tooltip={
                readOnly
                  ? intl.formatMessage({
                      id: 'XRayConfiguration.saveLibraryModel.readOnlyModeTooltip',
                      defaultMessage: 'You are in read-only mode.',
                      description:
                        'Tooltip for disabled save library model button in read-only mode.',
                    })
                  : isEvaluating // order matters
                    ? undefined
                    : isConfigDirty || hasNoTestCases
                      ? intl.formatMessage({
                          id: 'XRayConfiguration.saveLibraryModel.saveAndEvaluateTooltip',
                          defaultMessage: 'Save and Evaluate the model first.',
                          description:
                            'Tooltip for disabled save library model button when the model is not evaluated.',
                        })
                      : !configRequrementsMet
                        ? intl.formatMessage({
                            id: 'XRayConfiguration.saveLibraryModel.missingFieldsTooltip',
                            defaultMessage:
                              'Select a field and fill in instructions.',
                            description:
                              'Tooltip for disabled save library model button when field and instructions are not set.',
                          })
                        : isMissingDropdownOptions
                          ? intl.formatMessage({
                              id: 'XRayConfiguration.saveLibraryModel.missingDropdownOptionsTooltip',
                              defaultMessage:
                                'Provide at least one dropdown option and the not applicable option, or choose Create with AI.',
                              description:
                                'Tooltip for disabled save library model button when dropdown options are missing.',
                            })
                          : undefined
              }
            />
          )}
        </Layout>
      </Layout>
      {confirmFieldSelectModal}
      {isCreateFieldPanelVisible && (
        <CreateFieldPanel
          acceptFieldTypes={[
            DataFieldType.ARRAY_MULTIPLE,
            DataFieldType.ARRAY_SINGLE,
            DataFieldType.DATE,
            DataFieldType.NUMBER,
            DataFieldType.STRING,
            DataFieldType.TEXT_AREA,
          ]}
          onCreate={(newField, fieldData) => {
            let fieldClassification;
            if (testIsClassificationModel(newField)) {
              fieldClassification = {
                isMulti: fieldData.fieldConfig.enableMultiple,
                options: fieldData.fieldConfig.optionValues.map((value) => ({
                  label: value,
                  value,
                })),
              };
            }
            dispatch(
              slices.xRay.actions.setField({
                field: newField,
                fieldClassification,
              }),
            );
          }}
          onHide={hideCreateFieldPanel}
        />
      )}
    </Section>
  );
};
